/**
 * SubscriptionPage — the lab's READ view of its central entitlement (plan / statut / modules cloud-vs-
 * local) + the derived health block + the OUTBOUND change-request action. The central Pharmakon ADMIN
 * is authoritative; the lab holds a read-only projection and may only REQUEST a change. FAIL-OPEN: an
 * unsynced lab still renders (a "not yet synced" notice, features available). The change-request channel
 * is config-gated server-side — a 501 surfaces as a "channel not enabled" notice, never a fake success.
 *
 * Gating: read on lab_admin.abonnement.read (screen ForbiddenState); the change-request CTA/modal on
 * lab_admin.abonnement.request via useCan (the server re-checks). No PHI on this seam (L-4).
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  Modal,
  OfflineState,
  Select,
  Textarea,
  useToast,
  type BadgeTone,
} from "@/ui";
import { ApiError } from "@/api";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ADMIN_CAPS } from "../caps";
import { useLabId, useRequestChange, useSubscription } from "../api";
import type { ChangeCycle, ChangeRequestType, Subscription } from "../types";
import { PageHeader } from "../components/PageHeader";
import { Screen, TileGrid } from "../components/Screen";
import { formatDateTime, statutTone } from "../lib";

export function SubscriptionPage() {
  const { t, locale } = useT();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(ADMIN_CAPS.abonnementRead);
  const canRequest = useCan(ADMIN_CAPS.abonnementRequest);

  const query = useSubscription(labId, canRead && online);
  const [requestOpen, setRequestOpen] = useState(false);

  const header = (
    <PageHeader
      title={t("admin.subscription.title")}
      subtitle={t("admin.subscription.subtitle")}
      actions={
        canRequest ? (
          <Button onClick={() => setRequestOpen(true)}>{t("admin.subscription.request.cta")}</Button>
        ) : undefined
      }
    />
  );

  if (!canRead) return <ForbiddenState requiredCapabilities={[ADMIN_CAPS.abonnementRead]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={!!query.data} />
      </Screen>
    );
  }
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      </Screen>
    );
  }

  const sub = query.data as Subscription;

  return (
    <Screen>
      {header}

      <TileGrid min={220}>
        <Card title={t("admin.subscription.plan")}>
          <div style={{ fontSize: "var(--nv-fs-h2)", fontWeight: 600 }}>{sub.plan ?? "—"}</div>
        </Card>
        <Card title={t("admin.subscription.statut")}>
          <Badge tone={statutTone(sub.statut)} glyph="●">
            {sub.statut ?? t("admin.subscription.health.notSynced")}
          </Badge>
        </Card>
        <Card title={t("admin.subscription.health.title")}>
          <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap", alignItems: "center" }}>
            <Badge tone={sub.health.active ? "success" : "warn"} glyph="●">
              {sub.health.active ? t("admin.subscription.health.active") : t("admin.subscription.health.inactive")}
            </Badge>
            <Badge tone={sub.health.synced ? "info" : "neutral"} glyph="◇">
              {sub.health.synced ? t("admin.subscription.health.synced") : t("admin.subscription.health.notSynced")}
            </Badge>
          </div>
        </Card>
      </TileGrid>

      {!sub.synced && (
        <Card>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
            <strong>{t("admin.subscription.notSynced.title")}</strong>
            <span style={{ color: "var(--nv-text-2, var(--nv-slate))" }}>
              {t("admin.subscription.notSynced.desc")}
            </span>
          </div>
        </Card>
      )}

      <Card>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{sub.health.message}</p>
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-4)", marginTop: "var(--nv-s-3)", marginBottom: 0 }}>
          {sub.reason && (
            <>
              <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.subscription.reason")}</dt>
              <dd style={{ margin: 0 }}>{sub.reason}</dd>
            </>
          )}
          {sub.version !== undefined && (
            <>
              <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.subscription.version")}</dt>
              <dd style={{ margin: 0 }} className="nv-ltr-island">{sub.version}</dd>
            </>
          )}
          {sub.projectedAt && (
            <>
              <dt style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{t("admin.subscription.projectedAt")}</dt>
              <dd style={{ margin: 0 }} className="nv-ltr-island">{formatDateTime(sub.projectedAt, locale)}</dd>
            </>
          )}
        </dl>
      </Card>

      <TileGrid min={280}>
        <ModuleList
          title={t("admin.subscription.modules.cloud")}
          hint={t("admin.subscription.modules.cloud.hint")}
          modules={sub.modulesCloud}
          tone={sub.health.active ? "info" : "warn"}
          emptyLabel={t("admin.subscription.modules.empty")}
        />
        <ModuleList
          title={t("admin.subscription.modules.local")}
          hint={t("admin.subscription.modules.local.hint")}
          modules={sub.modulesLocal}
          tone="success"
          emptyLabel={t("admin.subscription.modules.empty")}
        />
      </TileGrid>

      {canRequest && (
        <ChangeRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} labId={labId} />
      )}
    </Screen>
  );
}

function ModuleList({
  title,
  hint,
  modules,
  tone,
  emptyLabel,
}: {
  title: string;
  hint: string;
  modules: string[];
  tone: BadgeTone;
  emptyLabel: string;
}) {
  return (
    <Card title={title}>
      <p style={{ marginTop: 0, color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
        {hint}
      </p>
      {modules.length === 0 ? (
        <span style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{emptyLabel}</span>
      ) : (
        <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
          {modules.map((m) => (
            <Badge key={m} tone={tone} glyph="▣">
              <span className="nv-ltr-island">{m}</span>
            </Badge>
          ))}
        </div>
      )}
    </Card>
  );
}

function ChangeRequestModal({
  open,
  onClose,
  labId,
}: {
  open: boolean;
  onClose: () => void;
  labId: string | null;
}) {
  const { t } = useT();
  const toast = useToast();
  const request = useRequestChange(labId);
  const [type, setType] = useState<ChangeRequestType>("upgrade");
  const [plan, setPlan] = useState("");
  const [cycle, setCycle] = useState<ChangeCycle | "">("");
  const [motif, setMotif] = useState("");
  const [error, setError] = useState<string | null>(null);

  const needsPlan = type === "upgrade" || type === "downgrade";

  const reset = () => {
    setType("upgrade");
    setPlan("");
    setCycle("");
    setMotif("");
    setError(null);
  };

  const submit = () => {
    setError(null);
    if (needsPlan && !plan.trim()) {
      setError(t("admin.subscription.request.planRequired"));
      return;
    }
    request.mutate(
      {
        type,
        ...(needsPlan ? { plan: plan.trim() } : {}),
        ...(cycle ? { cycle } : {}),
        ...(motif.trim() ? { motif: motif.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(res.reused ? t("admin.subscription.request.reused") : t("admin.subscription.request.queued"));
          reset();
          onClose();
        },
        onError: (err) => {
          // 501 NOT_IMPLEMENTED — the channel is not yet enabled centrally. Surface, never fake-succeed.
          if (err instanceof ApiError && err.httpStatus === 501) {
            setError(t("admin.subscription.request.notEnabled"));
            return;
          }
          setError(err instanceof ApiError ? err.message : t("admin.toast.error"));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("admin.subscription.request.title")}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t("admin.action.cancel")}
          </Button>
          <Button onClick={submit} loading={request.isPending}>
            {t("admin.subscription.request.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <FormField label={t("admin.subscription.request.type")}>
          {(p) => (
            <Select
              {...p}
              value={type}
              onChange={(e) => setType(e.target.value as ChangeRequestType)}
              options={[
                { value: "upgrade", label: t("admin.subscription.request.type.upgrade") },
                { value: "downgrade", label: t("admin.subscription.request.type.downgrade") },
                { value: "cancel", label: t("admin.subscription.request.type.cancel") },
              ]}
            />
          )}
        </FormField>

        {needsPlan && (
          <FormField label={t("admin.subscription.request.plan")} required>
            {(p) => (
              <Input
                {...p}
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                placeholder={t("admin.subscription.request.plan.placeholder")}
              />
            )}
          </FormField>
        )}

        <FormField label={t("admin.subscription.request.cycle")}>
          {(p) => (
            <Select
              {...p}
              value={cycle}
              onChange={(e) => setCycle(e.target.value as ChangeCycle | "")}
              placeholder="—"
              options={[
                { value: "mensuel", label: t("admin.subscription.request.cycle.mensuel") },
                { value: "annuel", label: t("admin.subscription.request.cycle.annuel") },
              ]}
            />
          )}
        </FormField>

        <FormField label={t("admin.subscription.request.motif")}>
          {(p) => <Textarea {...p} value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} />}
        </FormField>

        <p style={{ margin: 0, color: "var(--nv-text-3, var(--nv-steel))", fontSize: "var(--nv-fs-caption)" }}>
          {t("admin.subscription.request.note")}
        </p>

        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
